// @ts-nocheck
import { useEffect, useState } from 'react';
import { ownerApi } from './ownerApi';
import { useLoad } from './lib/useLoad';
import PageHeader from './ui/PageHeader';
import Card from './ui/Card';
import Button from './ui/Button';
import Alert from './ui/Alert';
import { SkeletonRows } from './ui/Skeleton';

const EFFORT_LABELS = {
  '': 'Automatic',
  none: 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const FIELD = 'mt-1 h-9 w-full rounded-lg border border-[#7C8798] px-3';

/** @param {any} catalog @param {string} provider */
function catalogModels(catalog, provider) {
  const rows = catalog?.[provider]?.models;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Live models for this provider that fit the role.
 * The current id stays in the list so a saved model is never a blank select.
 * @param {any} catalog
 * @param {string} provider
 * @param {string} kind
 * @param {string} current
 */
function modelsFor(catalog, provider, kind, current) {
  const rows = catalogModels(catalog, provider).filter((item) => {
    const kinds = item?.kinds;
    if (!Array.isArray(kinds) || kinds.length === 0) return true;
    return kinds.includes(kind);
  });
  if (current && !rows.some((item) => item.id === current)) {
    return [{ id: current, efforts: [] }, ...rows];
  }
  return rows;
}

/** @param {any} modelRow @param {string[] | undefined} roleEfforts */
function effortChoices(modelRow, roleEfforts) {
  const fromModel = modelRow?.efforts;
  const values = Array.isArray(fromModel) && fromModel.length
    ? fromModel
    : (Array.isArray(roleEfforts) && roleEfforts.length ? roleEfforts : ['', 'low', 'medium', 'high']);
  return values.map((value) => ({ value: String(value), label: EFFORT_LABELS[value] || String(value) }));
}

export default function OwnerAiModels() {
  const state = useLoad(() => ownerApi.aiModels(), []);
  const [roles, setRoles] = useState(/** @type {any[]} */ ([]));
  const [catalog, setCatalog] = useState(/** @type {any} */ (null));
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (state.status !== 'ready' || !state.data) return;
    setRoles(state.data.roles || []);
    setCatalog(state.data.catalog || null);
  }, [state.status, state.data]);

  const patch = (role, /** @type {Record<string, unknown>} */ changes) => {
    setSaved('');
    setRoles((current) => current.map((row) => (row.role === role ? { ...row, ...changes } : row)));
  };

  const refresh = async () => {
    setBusy('refresh');
    setError('');
    try {
      const data = await ownerApi.refreshAiModels();
      setCatalog(data.catalog || null);
      setRoles(data.roles || []);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Refresh failed');
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    setError('');
    setSaved('');
    /** @type {Record<string, Record<string, unknown>>} */
    const body = {};
    for (const row of roles) {
      const temperature = row.temperature === '' || row.temperature == null ? null : Number(row.temperature);
      const models = modelsFor(catalog, row.provider, row.kind, row.model);
      const chosen = models.find((item) => item.id === row.model);
      const allowed = new Set(effortChoices(chosen, row.supported_efforts).map((item) => item.value));
      body[row.role] = {
        provider: row.provider,
        model: row.model,
        reasoning_effort: allowed.has(String(row.reasoning_effort || '')) ? (row.reasoning_effort || '') : '',
        temperature: Number.isFinite(temperature) ? temperature : null,
        effort_locked: Boolean(row.effort_locked),
        temperature_locked: Boolean(row.temperature_locked) && temperature != null,
      };
    }
    try {
      const data = await ownerApi.saveAiModels(body);
      setRoles(data.roles || []);
      setCatalog(data.catalog || catalog);
      setSaved('Saved. The next message uses these models. No deploy is required.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Save failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI models"
        subtitle="Each job picks a provider, a model from that provider's live list, and a reasoning effort that model accepts."
        actions={<Button variant="secondary" onClick={() => void refresh()} disabled={!!busy}>{busy === 'refresh' ? 'Refreshing…' : 'Refresh model list'}</Button>}
      />
      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}
      {state.status === 'error' ? <Alert title="We couldn't load AI models." detail={state.error} /> : null}
      {error ? <Alert title="That change was not saved." detail={error} /> : null}
      {saved ? <p className="text-sm text-green-800">{saved}</p> : null}
      {roles.map((row) => {
        const options = modelsFor(catalog, row.provider, row.kind, row.model);
        const chosen = options.find((item) => item.id === row.model) || options[0];
        const efforts = effortChoices(chosen, row.supported_efforts);
        const effortValue = efforts.some((item) => item.value === String(row.reasoning_effort || ''))
          ? String(row.reasoning_effort || '')
          : '';
        const note = row.xai_supported ? '' : (row.support_note || 'not supported by provider');
        const onProvider = (/** @type {string} */ provider) => {
          const nextModels = modelsFor(catalog, provider, row.kind, '');
          const model = nextModels.some((item) => item.id === row.model) ? row.model : (nextModels[0]?.id || row.model);
          const nextChosen = nextModels.find((item) => item.id === model);
          const nextEfforts = effortChoices(nextChosen, row.supported_efforts);
          const effort = nextEfforts.some((item) => item.value === String(row.reasoning_effort || ''))
            ? (row.reasoning_effort || '')
            : '';
          patch(row.role, { provider, model, reasoning_effort: effort });
        };
        return (
          <Card key={row.role} title={row.label} subtitle={row.description || ''}>
            <p className="mb-3 text-xs text-slate-400">{row.role}</p>
            {note ? <p className="mb-3 text-sm text-amber-800">{note}</p> : null}
            <p className="mb-3 text-sm text-slate-600">{row.saved ? 'Saved in the portal' : `Default ${row.default_provider} / ${row.default_model}`}</p>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">Provider
                <select aria-label={`${row.label} provider`} className={FIELD} value={row.provider} onChange={(event) => onProvider(event.target.value)}>
                  <option value="openai">OpenAI</option>
                  <option value="xai" disabled={!row.xai_supported}>xAI{row.xai_supported ? '' : ' — not supported by provider'}</option>
                </select>
              </label>
              <label className="text-sm">Model
                <select aria-label={`${row.label} model`} className={FIELD} value={row.model || ''} onChange={(event) => {
                  const model = event.target.value;
                  const nextChosen = options.find((item) => item.id === model);
                  const nextEfforts = effortChoices(nextChosen, row.supported_efforts);
                  const effort = nextEfforts.some((item) => item.value === effortValue) ? effortValue : '';
                  patch(row.role, { model, reasoning_effort: effort });
                }}>
                  {options.map((item) => <option key={item.id} value={item.id}>{item.id}</option>)}
                </select>
              </label>
              <label className="text-sm">Reasoning effort
                <select aria-label={`${row.label} effort`} className={FIELD} value={effortValue} onChange={(event) => patch(row.role, { reasoning_effort: event.target.value })}>
                  {efforts.map((item) => <option key={item.value || 'auto'} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              <label className="text-sm">Temperature
                <input aria-label={`${row.label} temperature`} type="number" min="0" max="2" step="0.1" className={FIELD} value={row.temperature ?? ''} onChange={(event) => patch(row.role, { temperature: event.target.value })} />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(row.effort_locked)} onChange={(event) => patch(row.role, { effort_locked: event.target.checked })} /> Lock reasoning effort</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(row.temperature_locked)} onChange={(event) => patch(row.role, { temperature_locked: event.target.checked })} /> Lock temperature</label>
            </div>
            {row.role === 'owner_copilot' && !row.effort_locked ? <p className="mt-2 text-[13px] text-slate-600">Effort stays automatic: low for chat, high for work.</p> : null}
            {efforts.length === 1 ? <p className="mt-2 text-[13px] text-slate-600">This model does not use reasoning effort.</p> : null}
          </Card>
        );
      })}
      {roles.length ? <Button onClick={() => void save()} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save models'}</Button> : null}
    </div>
  );
}
